import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { TOTPVerify } from '../../components/auth/totp-verify'
import { processAuthLink, cleanupAuthLinkUrl } from '../../lib/auth-link-utils'
import { getUserProfile } from '../../lib/api/api' // For user profile

export function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [hasAccessToken, setHasAccessToken] = useState(false)
  const [needsMfa, setNeedsMfa] = useState(false)
  const [pendingPassword, setPendingPassword] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()

  useEffect(() => {
    const handleAuthFlow = async () => {
      console.log('[ResetPasswordPage] useEffect - handleAuthFlow called');
      console.log('[ResetPasswordPage] Processing auth link parameters');
      setIsLoading(true);
      
      try {
        const result = await processAuthLink(supabase, location);
        console.log('[ResetPasswordPage] processAuthLink completed with success:', result.success);
        
        if (result.success && result.session) {
          console.log('[ResetPasswordPage] Successfully authenticated via processAuthLink');
          setHasAccessToken(true);
          cleanupAuthLinkUrl(window.location, true); // Preserve hash for routing
          
          // Double check that the session is actually set in Supabase
          const { data: { session: verifySession } } = await supabase.auth.getSession();
          console.log('[ResetPasswordPage] Session verification completed, has session:', !!verifySession);
        } else {
          // If processAuthLink didn't establish a session (either error or no valid link data)
          // Check for an existing valid session if already on the reset page (e.g. page refresh after token set)
          console.log('[ResetPasswordPage] processAuthLink did not establish session, checking existing session');
          const { data: { session: existingSession } } = await supabase.auth.getSession();
          console.log('[ResetPasswordPage] Existing session check completed, has session:', !!existingSession);
          
          const isResetPasswordPath = location.hash.includes('/auth/reset-password') || 
                                      location.pathname.includes('/auth/reset-password');

          if (existingSession && isResetPasswordPath) {
            console.log('[ResetPasswordPage] Active existing session found on reset password page');
            setHasAccessToken(true);
          } else {
            console.log('[ResetPasswordPage] No valid link data and no active existing session on reset page.');
            setHasAccessToken(false);
            // Only show toast and navigate if there was an actual error from processAuthLink
            // or if it's clear that the link was invalid (no code, no token).
            if (result.error || (!new URLSearchParams(location.search).get('code') && !location.hash.includes('access_token'))) {
              console.log('[ResetPasswordPage] Invalid reset link, redirecting to login');
              toast({
                title: 'Error',
                description: result.error?.message || 'Invalid or expired password reset link. Please request a new password reset.',
                variant: 'destructive',
              });
              navigate('/auth/login', { replace: true });
            } else {
              console.log('[ResetPasswordPage] Auth params present but session not established - allowing form to show but it will fail');
            }
          }
        }
      } catch (error) { // Catch unexpected errors from processAuthLink or getSession
        console.error('[ResetPasswordPage] Unexpected error in handleAuthFlow:', error);
        setHasAccessToken(false);
        toast({
          title: 'Error',
          description: 'Failed to process password reset link. Please try again.',
          variant: 'destructive',
        });
        navigate('/auth/login', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthFlow();
  }, [navigate, toast, location, supabase]) // Added supabase to dependencies

  const validatePassword = (password: string): string | null => {
    if (password.length < 12) {
      return 'Password must be at least 12 characters long'
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number'
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return 'Password must contain at least one special character'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    // Validate password
    console.log('[ResetPasswordPage] handleSubmit called');

    console.log('[ResetPasswordPage] Validating password format');
    // Validate password
    const passwordError = validatePassword(password)
    if (passwordError) {
      console.log('[ResetPasswordPage] Password validation failed:', passwordError);
      setFormError(passwordError)
      return
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      console.log('[ResetPasswordPage] Passwords do not match.');
      setFormError('Passwords do not match')
      return
    }

    console.log('[ResetPasswordPage] Passwords match and validation passed. Setting isLoading to true.');
    setIsLoading(true)
    try {
      // Check if we have a session before attempting password update
      console.log('[ResetPasswordPage] Checking current session before updateUser');
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
      console.log('[ResetPasswordPage] Current session exists:', !!currentSession);
      console.log('[ResetPasswordPage] Session error:', sessionError);
      
      if (!currentSession) {
        console.error('[ResetPasswordPage] No session found when trying to update password');
        
        // Try to reprocess the auth link to restore the session
        console.log('[ResetPasswordPage] Attempting to restore session from auth link');
        const result = await processAuthLink(supabase, location);
        console.log('[ResetPasswordPage] Restore session completed with success:', result.success);
        
        if (result.success && result.session) {
          console.log('[ResetPasswordPage] Successfully restored session, proceeding with password update');
        } else {
          throw new Error('No active session found. Please try the password reset link again.');
        }
      }
      
      console.log('[ResetPasswordPage] Attempting supabase.auth.updateUser');
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        console.error('[ResetPasswordPage] Error from supabase.auth.updateUser:', error);
        // Check if the error is due to MFA requirement
        if (error.message.includes('AAL2 session is required')) {
          console.log('[ResetPasswordPage] MFA is required. Setting needsMfa to true.');
          setPendingPassword(password)
          setNeedsMfa(true)
          return
        }
        throw error
      }

      console.log('[ResetPasswordPage] supabase.auth.updateUser successful.');
      // If we get here, password was updated successfully
      await handleSuccessfulPasswordReset()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
      console.error('[ResetPasswordPage] Catch block in handleSubmit:', errorMessage, error);
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuccessfulPasswordReset = async () => {
    console.log('[ResetPasswordPage] handleSuccessfulPasswordReset called');
    // Since this is partner-ui, all users are partner users, so just sign out and redirect to partner login
    console.log('[ResetPasswordPage] Signing out user.');
    await supabase.auth.signOut();
    
    toast({
      title: 'Success',
      description: 'Your password has been reset successfully. Please log in with your new password.',
    });
    
    // Use replace to prevent back navigation to the reset page
    console.log('[ResetPasswordPage] Navigating to partner login page.');
    navigate('/auth/login', { replace: true });
  };

  const handleMfaComplete = async () => {
    console.log('[ResetPasswordPage] handleMfaComplete called');
    setIsLoading(true)
    try {
      console.log('[ResetPasswordPage] Attempting supabase.auth.updateUser after MFA verification');
      // Try updating password again after MFA verification
      const { error } = await supabase.auth.updateUser({ password: pendingPassword })
      if (error) {
        console.error('[ResetPasswordPage] Error from supabase.auth.updateUser in handleMfaComplete:', error);
        throw error
      }

      console.log('[ResetPasswordPage] supabase.auth.updateUser successful in handleMfaComplete.');
      // Reuse the updated handleSuccessfulPasswordReset logic
      await handleSuccessfulPasswordReset(); 
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
      console.error('[ResetPasswordPage] Catch block in handleMfaComplete:', errorMessage, error);
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
      setNeedsMfa(false)
      setPendingPassword('')
    }
  }

  return (
    <div className={`flex min-h-screen items-center justify-center ${getThemeClass('gradients.primary')}`}>
      <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
        <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>Set New Password</h1>
        {!hasAccessToken ? (
          <Alert variant="destructive">
            <AlertDescription>Invalid or expired password reset link. Please request a new password reset.</AlertDescription>
          </Alert>
        ) : needsMfa ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                Two-factor authentication is required to complete your password reset.
              </AlertDescription>
            </Alert>
            <TOTPVerify onComplete={handleMfaComplete} />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                className={getThemeClass('components.input.base')}
              />
            </div>
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <div className="text-sm text-gray-600">
              Password must:
              <ul className="list-disc pl-5 mt-1">
                <li>Be at least 12 characters long</li>
                <li>Contain at least one uppercase letter</li>
                <li>Contain at least one lowercase letter</li>
                <li>Contain at least one number</li>
                <li>Contain at least one special character</li>
              </ul>
            </div>
            <Button
              type="submit"
              className={`w-full ${getThemeClass('components.button.primary')}`}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  )
}
